import { useMemo } from "react";
import Utils, { IS_EYEJACK_APP } from "@webcore/core/utils/utils";
import { USER_ID_KEY, SESSION_ID_KEY } from "./useAppClipUrl";

/**
 * Cross-context analytics identity (web/Safari <-> iOS App Clip).
 *
 * The App Clip runs in its own storage sandbox, so it can't see the web page's
 * cookies/localStorage. `buildAppClipUrl` forwards the web `userId`/`sessionId`
 * in the App Clip URL; this hook re-hydrates them so both contexts report the
 * SAME ids and their events group into one journey in Looker/BigQuery.
 *
 * Attach the returned ids to your analytics config/events, e.g.:
 *   const { userId, sessionId } = useAnalyticsIdentity();
 *   useAnalytics({ user_id: userId, session_id: sessionId });
 *
 * Storage keys are shared with `useAppClipUrl` (USER_ID_KEY / SESSION_ID_KEY),
 * so the id stored here is exactly the one forwarded into the App Clip URL. The
 * URL param names below match the keys `buildAppClipUrl` writes.
 */

const USER_ID_PARAM = "userId";
const SESSION_ID_PARAM = "sessionId";

export interface AnalyticsIdentity {
  userId: string;
  sessionId: string;
}

/**
 * Resolve a stable id: inside the App Clip the id handed over in the URL wins
 * (linking back to the originating web session); otherwise use the stored value,
 * minting and persisting a fresh one only when none exists yet.
 */
function resolveId(
  store: Storage,
  key: string,
  urlParam: string,
  preferUrl: boolean
): string {
  try {
    if (preferUrl) {
      const fromUrl = new URLSearchParams(window.location.search).get(urlParam);
      if (fromUrl) {
        store.setItem(key, fromUrl);
        return fromUrl;
      }
    }
    const stored = store.getItem(key);
    if (stored) return stored;
    const fresh = Utils.UUIDShort();
    store.setItem(key, fresh);
    return fresh;
  } catch {
    // Private mode / storage disabled — ephemeral id; events still group within
    // the current page load.
    return Utils.UUIDShort();
  }
}

export function useAnalyticsIdentity(): AnalyticsIdentity {
  // IS_EYEJACK_APP is a load-time constant, so resolve once per mount.
  return useMemo<AnalyticsIdentity>(
    () => ({
      userId: resolveId(window.localStorage, USER_ID_KEY, USER_ID_PARAM, IS_EYEJACK_APP),
      sessionId: resolveId(window.sessionStorage, SESSION_ID_KEY, SESSION_ID_PARAM, IS_EYEJACK_APP),
    }),
    []
  );
}

export default useAnalyticsIdentity;
